import { FC, useContext, useState, ReactNode, useMemo, useEffect } from "react";
import QuestContext from "../../../../components/QuestWrapper";
import { DailyStreakProps, StepDetail } from "../component/StreakTypes";
import "./streak.css";
import StreakThemeManager from "../component/StreakThemeManager";
import { StreakContext } from "../modular/StreakContextType";
import { StreakCounter } from "../modular/StreakCounter";
import { StreakContent } from "../modular/StreakContent";
import { StreakFooter } from "../modular/StreakFooter";
import { StreakTimer } from "../modular/StreakTimer";

interface StreakComponentChildren {
    Counter: typeof StreakCounter;
    Content: typeof StreakContent;
    Timer: typeof StreakCounter;
    Footer: typeof StreakFooter;
}

interface StreakComponents extends DailyStreakProps {
    children?: ReactNode;
}

const defaultStepDetails: StepDetail[] = [
    { range: 1, title: "Day 1", description: "" },
    { range: 2, title: "Day 2", description: "" },
    { range: 3, title: "Day 3", description: "" },
    { range: 4, title: "Day 4", description: "" },
    { range: 5, title: "Day 5", description: "" },
];

const StreakOffline: FC<StreakComponents> & StreakComponentChildren = ({
    pendingStreakImg = "",
    filledStreakImg = "",
    counterId = "",
    userId = "",
    token = "",
    counter = 10,
    stepDetails = defaultStepDetails,
    uniqueEmailId,
    uniqueUserId,
    styleConfig,
    footerText,
    footerLink,
    variation,
    brandTheme: initialBrandTheme,
    QuestThemeData: initialQuestThemeData,
    children,
    checkedUponCompletion,
    confettiOnCompletion,
    confettiDuration,
    showFooter = true,
    ...props
}) => {
    const [days, setDays] = useState(counter);
    const [steps, setSteps] = useState(() => stepDetails.sort((a, b) => a.range - b.range));
    useEffect(() => {
        setSteps(stepDetails.sort((a, b) => a.range - b.range));
    },[stepDetails])
    const { themeConfig } = useContext(
        QuestContext.Context
    );

    // Create theme manager using useMemo
    const themeManager = useMemo(() => {
        return new StreakThemeManager(
            themeConfig,
            styleConfig,
            initialQuestThemeData,
            initialBrandTheme
        );
    }, [themeConfig, styleConfig, initialQuestThemeData, initialBrandTheme]);

    const getCurrentStep = (days: number): number => {
        for (let i = 0; i < steps.length; i++) {
            if (days < steps[i].range) {
                return Math.max(0, i - 1);
            }
        }
        return steps.length - 1;
    };

    const isStepActive = (index: number): boolean => {
        return days >= steps[index].range;
    };

    const currentStep = getCurrentStep(days);

    const contextValue = {
        days,
        currentStep,
        themeManager,
        stepDetails,
        isStepActive,
        pendingStreakImg,
        filledStreakImg,
        footerText,
        footerLink,
        checkedUponCompletion,
        showFooter
    };

    return (
        <StreakContext.Provider value={contextValue}>
            <div
                className="q_daily_streak_cont"
                style={themeManager.getContainerStyles()}
                {...props}
            >
                {children}
            </div>
        </StreakContext.Provider>
    );
};

StreakOffline.Counter = StreakCounter;
StreakOffline.Content = StreakContent;
StreakOffline.Timer = StreakTimer;
StreakOffline.Footer = StreakFooter;

export default StreakOffline;